// Shared parsing/formatting for the donor delivery config rules stored as JSON strings on
// Client.email_configurations / Client.reactivation_configurations / Client.sms_configurations.
// Shape: [{ id, type: 'floor'|'target'|'ceiling', proportion: 0-1, rank,
//   details: { donor_type, days } }]

export const TYPE_META = {
  floor: { label: 'Floor', color: '#5B6270' },
  target: { label: 'Target', color: '#127A66' },
  ceiling: { label: 'Ceiling', color: '#A9720A' },
};

// ~10% opacity tint of the rule's own color, so the chip background blends with whatever
// is underneath it (row stripe, hover, expanded highlight) instead of sitting as a flat,
// unresponsive block on top of it.
const BG_ALPHA_SUFFIX = '1A';

export const CATEGORY_LABEL = {
  all: 'All',
  political: 'Political',
  nonprofit: 'Nonprofit',
  cross_sector: 'Cross Sector',
};

export const CATEGORY_INITIAL = {
  all: 'A',
  political: 'P',
  nonprofit: 'N',
  cross_sector: 'X',
};

// Filter dropdown options, matching the donor_config_type/donor_category/donor_recency
// picklists in settingConstants.js.
export const TYPE_FILTER_OPTIONS = [
  { value: 'floor', label: TYPE_META.floor.label },
  { value: 'target', label: TYPE_META.target.label },
  { value: 'ceiling', label: TYPE_META.ceiling.label },
];

export const CATEGORY_FILTER_OPTIONS = [
  { value: 'all', label: CATEGORY_LABEL.all },
  { value: 'political', label: CATEGORY_LABEL.political },
  { value: 'nonprofit', label: CATEGORY_LABEL.nonprofit },
  { value: 'cross_sector', label: CATEGORY_LABEL.cross_sector },
];

export const RECENCY_FILTER_OPTIONS = [15, 30, 45, 60, 90].map((days) => ({
  value: days,
  label: `≤ ${days}d`,
}));

export const ANY_FILTER_VALUE = 'Any';

export const DEFAULT_CONFIG_FILTERS = {
  category: ANY_FILTER_VALUE,
  type: ANY_FILTER_VALUE,
  recency: ANY_FILTER_VALUE,
};

/**
 * Parses a client's donor delivery config field (JSON string, array, or null/undefined)
 * into a normalized, rank-sorted array of display-ready rule objects.
 * @param {string|Array|null|undefined} raw - the raw config field value from a Client record
 * @returns {Array} rank-sorted rule objects with type/category/proportion/recency display fields
 */
export function parseDonorConfigs(raw) {
  let configs = raw;
  if (typeof configs === 'string') {
    try {
      configs = JSON.parse(configs);
    } catch (e) {
      configs = [];
    }
  }
  if (!Array.isArray(configs)) {
    configs = [];
  }

  return configs
    .slice()
    .sort((a, b) => (a.rank || 0) - (b.rank || 0))
    .map((cfg) => {
      const meta = TYPE_META[cfg.type] || TYPE_META.target;
      const category = (cfg.details && cfg.details.donor_type) || 'all';
      const days = cfg.details ? cfg.details.days : null;
      return {
        rank: cfg.rank,
        type: cfg.type,
        typeLabel: meta.label,
        color: meta.color,
        bg: `${meta.color}${BG_ALPHA_SUFFIX}`,
        category,
        categoryLabel: CATEGORY_LABEL[category] || category,
        categoryInitial: CATEGORY_INITIAL[category] || '?',
        proportionPct: Math.round((cfg.proportion || 0) * 100),
        recencyDays: days || null,
        recencyLabel: days ? `${days}d` : 'no rec.',
      };
    });
}

/**
 * Formats a parsed rule into the compact chip label used across the Delivery Settings view,
 * e.g. "1 20% P·30d" (rank, proportion, category initial, recency).
 * @param {object} rule - a rule object returned by parseDonorConfigs
 * @returns {string} the compact chip label
 */
export function formatConfigChipLabel(rule) {
  return `${rule.rank} ${rule.proportionPct}% ${rule.categoryInitial}·${rule.recencyLabel}`;
}

/**
 * Formats a parsed rule's proportion/category/recency, without the rank - for use where
 * rank is rendered separately (e.g. as its own numbered badge), so it doesn't read as
 * just another number in the string.
 * @param {object} rule - a rule object returned by parseDonorConfigs
 * @returns {string} e.g. "20% P·30d"
 */
export function formatConfigChipDetail(rule) {
  return `${rule.proportionPct}% ${rule.categoryInitial}·${rule.recencyLabel}`;
}

/**
 * Checks a single parsed rule against the Filter Configs selections. A filter set to
 * ANY_FILTER_VALUE ('Any') is treated as unset. Recency is an "at most N days" match -
 * a rule with no recency window set never matches a specific recency filter.
 * @param {object} rule - a rule object returned by parseDonorConfigs
 * @param {{category: string, type: string, recency: string|number}} filters
 * @returns {boolean}
 */
export function ruleMatchesFilters(rule, filters) {
  const { category, type, recency } = { ...DEFAULT_CONFIG_FILTERS, ...filters };
  if (category !== ANY_FILTER_VALUE && rule.category !== category) return false;
  if (type !== ANY_FILTER_VALUE && rule.type !== type) return false;
  if (recency !== ANY_FILTER_VALUE) {
    if (rule.recencyDays === null || rule.recencyDays > Number(recency)) return false;
  }
  return true;
}

/**
 * Checks whether any rule across a set of raw donor config fields (e.g. a client's
 * email_configurations and reactivation_configurations) matches the given filters.
 * @param {Array<string|Array|null|undefined>} rawConfigsList - raw config field values
 * @param {{category: string, type: string, recency: string|number}} filters
 * @returns {boolean}
 */
export function anyRuleMatchesFilters(rawConfigsList, filters) {
  return rawConfigsList.some(
    (raw) => parseDonorConfigs(raw).some((rule) => ruleMatchesFilters(rule, filters)),
  );
}
